import React, { memo, useMemo } from 'react';

const RICH_PREVIEW_CHAR_LIMIT = 20000;
const RICH_PREVIEW_LINE_LIMIT = 450;

const TOKEN_PATTERNS = [
  ['comment', /^(\/\/.*|\/\*[\s\S]*?\*\/)/],
  ['string', /^(`(?:\\.|[^`])*`|'(?:\\.|[^'])*'|"(?:\\.|[^"])*")/],
  ['number', /^\b\d+(?:\.\d+)?\b/],
  ['keyword', /^\b(?:async|await|break|case|catch|class|const|constructor|continue|default|describe|else|export|extends|finally|for|from|function|if|import|in|it|let|new|null|return|test|this|throw|try|type|undefined|var|while)\b/],
  ['boolean', /^\b(?:false|true)\b/],
  ['builtin', /^\b(?:Array|Boolean|Date|Error|Map|Math|Number|Object|Promise|Set|String|URL|console|expect|page|testInfo)\b/],
  ['function', /^\b[A-Za-z_$][\w$]*(?=\s*\()/],
  ['property', /^\.[A-Za-z_$][\w$]*/],
  ['operator', /^[{}[\]();:,.<>+\-*/%=!&|?]+/],
];

const JSON_TOKEN_PATTERNS = [
  ['property', /^"(?:\\.|[^"])*"(?=\s*:)/],
  ['string', /^"(?:\\.|[^"])*"/],
  ['number', /^-?\b\d+(?:\.\d+)?(?:e[+-]?\d+)?\b/i],
  ['boolean', /^\b(?:false|true|null)\b/],
  ['operator', /^[{}[\]:,]/],
];

const TOKEN_STYLES = {
  keyword: { color: '#c792ea' },
  string: { color: '#ecc48d' },
  number: { color: '#f78c6c' },
  boolean: { color: '#f78c6c' },
  comment: { color: '#637777', fontStyle: 'italic' },
  function: { color: '#82aaff' },
  property: { color: '#addb67' },
  builtin: { color: '#ffcb6b' },
  operator: { color: '#89ddff' },
};

const getPatterns = (language) => (
  String(language || '').toLowerCase() === 'json' ? JSON_TOKEN_PATTERNS : TOKEN_PATTERNS
);

const tokenizeLine = (line, language) => {
  const tokens = [];
  let remaining = line || '';
  const patterns = getPatterns(language);

  while (remaining.length > 0) {
    const whitespace = remaining.match(/^\s+/);
    if (whitespace) {
      tokens.push({ type: 'plain', value: whitespace[0] });
      remaining = remaining.slice(whitespace[0].length);
      continue;
    }

    let match = null;
    for (const [type, pattern] of patterns) {
      const result = remaining.match(pattern);
      if (result) {
        match = { type, value: result[0] };
        break;
      }
    }

    if (match) {
      tokens.push(match);
      remaining = remaining.slice(match.value.length);
      continue;
    }

    tokens.push({ type: 'plain', value: remaining[0] });
    remaining = remaining.slice(1);
  }

  return tokens;
};

const lineCountFor = (value = '') => Math.max(1, String(value || '').split('\n').length);

const shouldUsePlainPreview = (value = '') => {
  const code = String(value || '');
  return code.length > RICH_PREVIEW_CHAR_LIMIT || lineCountFor(code) > RICH_PREVIEW_LINE_LIMIT;
};

export const CodeEditorPreview = memo(({
  value = '',
  language = 'typescript',
  editable = false,
  onChange,
  minHeight = 240,
  maxHeight = 520,
  className = '',
  placeholder = '',
}) => {
  const code = String(value || '');
  const lineCount = lineCountFor(code);
  const lines = useMemo(() => code.split('\n'), [code]);
  const lineNumbers = useMemo(() => Array.from({ length: lineCount }, (_, index) => index + 1), [lineCount]);
  const highlightedLines = useMemo(() => {
    if (editable || shouldUsePlainPreview(code)) return null;
    return lines.map((line) => tokenizeLine(line, language));
  }, [code, editable, language, lines]);
  const resolvedMinHeight = typeof minHeight === 'number' ? minHeight : Number.parseInt(minHeight, 10) || 240;
  const editorContentHeight = Math.max(resolvedMinHeight, (lineCount * 21) + 32);

  if (editable) {
    return (
      <div
        className={`code-editor-preview ${className}`}
        style={{ maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }}
      >
        <div className="code-editor-gutter" aria-hidden="true">
          {lineNumbers.map((lineNumber) => (
            <span key={lineNumber}>{lineNumber}</span>
          ))}
        </div>
        <textarea
          value={code}
          onChange={(event) => onChange?.(event.target.value)}
          spellCheck={false}
          placeholder={placeholder}
          className="code-editor-textarea"
          style={{ minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight, height: `${editorContentHeight}px` }}
        />
      </div>
    );
  }

  if (shouldUsePlainPreview(code)) {
    return (
      <textarea
        readOnly
        value={code}
        spellCheck={false}
        placeholder={placeholder}
        className={`code-editor-preview w-full resize-none font-mono ${className}`}
        style={{
          display: 'block',
          gridTemplateColumns: 'none',
          minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
          height: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
          maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
          padding: '1rem',
          whiteSpace: 'pre',
        }}
      />
    );
  }

  return (
    <div
      className={`code-editor-preview ${className}`}
      style={{ maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }}
    >
      <div className="code-editor-gutter" aria-hidden="true">
        {lineNumbers.map((lineNumber) => (
          <span key={lineNumber}>{lineNumber}</span>
        ))}
      </div>
      <pre className="code-editor-code">
        <code>
          {lines.map((line, lineIndex) => (
            <span className="code-editor-line" key={`${lineIndex}-${line}`}>
              {(highlightedLines?.[lineIndex] || []).map((token, tokenIndex) => (
                <span
                  style={TOKEN_STYLES[token.type] || undefined}
                  key={`${tokenIndex}-${token.value}`}
                >
                  {token.value}
                </span>
              ))}
            </span>
          ))}
        </code>
      </pre>
    </div>
  );
});
